import * as z from "zod";
import { isoDate } from "./common";

/**
 * The fees a customer pays on top of a Stripe card sale, for one payment path.
 * Basis points (100 = 1%) and minor units, so a client can name them without
 * rounding anything.
 */
export const FeeRates = z.object({
  /** HobbyScan technology fee on the pre-tax subtotal. */
  technologyFeeBps: z.number(),
  /** Stripe's percentage of the whole charge. */
  processingFeeBps: z.number(),
  /** Stripe's fixed charge per payment. */
  processingFeeFixedCents: z.number(),
});
export type FeeRates = z.output<typeof FeeRates>;

/** The whole back-office policy: one technology fee, the online pair and the in-person pair. */
export const FeePolicy = z.object({
  technologyFeeBps: z.number(),
  processingFeeBps: z.number(),
  processingFeeFixedCents: z.number(),
  terminalProcessingFeeBps: z.number(),
  terminalProcessingFeeFixedCents: z.number(),
  /** Live breaks: card fee added to the buyer's charge on the spots. */
  breakBuyerFeeBps: z.number(),
  /** Live breaks: the platform's cut of the seller's spots. */
  breakSellerFeeBps: z.number(),
  /** Null while the defaults apply and nobody has saved a policy. */
  updatedAt: isoDate.nullable(),
});
export type FeePolicy = z.output<typeof FeePolicy>;

export type FeePolicyInput = Omit<FeePolicy, "updatedAt">;

/** What paid Stripe checkouts added up to over one window, the two fees kept apart. */
export const FeeTotals = z.object({
  paidCheckouts: z.number(),
  grossCents: z.number(),
  merchantCents: z.number(),
  technologyFeeCents: z.number(),
  processingFeeCents: z.number(),
});
export type FeeTotals = z.output<typeof FeeTotals>;

export const AdminFees = z.object({
  policy: FeePolicy,
  last30Days: FeeTotals,
  allTime: FeeTotals,
});
export type AdminFees = z.output<typeof AdminFees>;

/** "1%", "1.5%" */
export function percentLabel(bps: number): string {
  return `${bps / 100}%`;
}

/** "2.9% + $0.30" */
export function processingRateLabel(rates: { processingFeeBps: number; processingFeeFixedCents: number }): string {
  const pct = percentLabel(rates.processingFeeBps);
  if (rates.processingFeeFixedCents === 0) return pct;
  return `${pct} + $${(rates.processingFeeFixedCents / 100).toFixed(2)}`;
}
